import React from 'react';
import { motion } from 'framer-motion';
import { ArrowTrendingUpIcon, ShieldCheckIcon, BeakerIcon, GlobeEuropeAfricaIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const FoodProcessing = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const processingSolutions = [
        "Cleaning, grading & sorting systems",
        "Primary & secondary processing",
        "Dehydration & preservation techniques",
        "Quality control & safety standards",
        "Packaging & branding solutions",
        "Shelf-life enhancement strategies"
    ];

    const valueAdditionList = [
        { title: "Processed food ingredients", icon: BeakerIcon },
        { title: "Packaged consumer products", icon: ShoppingBagIcon },
        { title: "Export-ready agricultural goods", icon: GlobeEuropeAfricaIcon },
        { title: "Branded farm-based products", icon: ShieldCheckIcon }
    ];

    const whyItMatters = [
        "Reduces post-harvest losses",
        "Extends product shelf life",
        "Increases farmer income",
        "Opens national & international markets",
        "Strengthens agri-business sustainability"
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas website food processing photo.webp')}
                        alt="Agriculture Food Processing"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Agriculture <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Food Processing</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        From Harvest to High-Value Products.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <strong className="text-gray-900 font-bold">Marvelous Agri Solution</strong>, Agriculture Food Processing transforms raw agricultural produce into premium, market-ready products with enhanced value, longer shelf life, and stronger consumer appeal.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We bridge the gap between farm production and modern food markets through scientific processing and quality-focused systems.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                Because real profitability comes from value creation.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Our Food Processing Solutions
                        </h3>

                        <div className="space-y-4 mb-8">
                            {processingSolutions.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            Every process is designed to maintain nutritional integrity and product quality.
                        </p>

                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                            Why Food Processing Matters
                        </h3>

                        <div className="mt-8 space-y-4">
                            {whyItMatters.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <CheckCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                    <span className="text-gray-700 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                    </motion.div>

                    <div className="lg:w-1/2 relative space-y-12">
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <img src={require('../../../assets/newimages/mas website foods processing.webp')} alt="Processing 1" className="rounded-2xl w-full h-48 object-cover shadow-lg" />
                            <img src={require('../../../assets/newimages/website food processing mas.webp')} alt="Processing 2" className="rounded-2xl w-full h-48 object-cover shadow-lg translate-y-6" />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Value Addition with Precision
                            </h3>

                            <p className="text-gray-600 mb-8 font-light">
                                We help convert raw produce into:
                            </p>

                            <div className="space-y-4 mb-8">
                                {valueAdditionList.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 hover:bg-emerald-50 rounded-xl transition-colors">
                                        <div className="bg-emerald-100 p-3 rounded-xl flex-shrink-0">
                                            <item.icon className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <p className="text-gray-800 font-medium text-lg">{item.title}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gray-50 border-t-2 border-emerald-500 p-6 rounded-b-xl -mx-8 -mb-8 mt-8 flex items-center justify-center gap-3">
                                <ArrowTrendingUpIcon className="w-6 h-6 text-emerald-600" />
                                <p className="font-bold text-emerald-800 italic">
                                    Resulting in higher market value and expanded revenue opportunities.
                                </p>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="bg-gray-900 p-10 rounded-3xl shadow-2xl text-white relative overflow-hidden"
                        >
                            <div className="absolute -right-20 -top-20 w-80 h-80 bg-emerald-500/20 rounded-full blur-3xl text-white"></div>

                            <h3 className="text-3xl font-bold mb-8 flex items-center gap-3 relative z-10 text-emerald-400">
                                Beyond Farming
                            </h3>

                            <p className="text-xl font-light text-gray-300 relative z-10 mb-8">
                                Agriculture Food Processing turns farms into <span className="font-medium text-white border-b border-emerald-500 pb-1">value-driven enterprises</span>.
                            </p>

                            <div className="relative z-10 space-y-4 border-l-4 border-emerald-500 pl-6 py-2 mb-8">
                                <p className="text-lg font-light text-gray-300">
                                    Better preservation.
                                </p>
                                <p className="text-lg font-light text-gray-300">
                                    Stronger branding.
                                </p>
                                <p className="text-lg font-light text-gray-300">
                                    Higher profitability.
                                </p>
                            </div>

                            <div className="relative z-10 text-center bg-white/10 p-6 rounded-2xl backdrop-blur-sm border border-white/20">
                                <p className="text-xl font-bold text-emerald-400 mb-2">Processing for Progress.</p>
                                <p className="text-xl font-bold text-emerald-400">Value for Every Harvest.</p>
                            </div>

                        </motion.div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default FoodProcessing;
